import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatINR } from "@/lib/roles";
import { toast } from "sonner";
import { Waves, Save } from "lucide-react";

const initialForm = {
  consumer_name: "", consumer_email: "", consumer_phone: "",
  address: "", ward: "Ward-1",
  connection_type: "domestic", property_type: "residential",
  built_up_area: 1000, number_of_seats: 2, connection_size_mm: 100,
  municipal_body: "Ludhiana Municipal Corporation",
};

export default function SewerageRegister() {
  const nav = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [wards, setWards] = useState([]);
  const [bodies, setBodies] = useState([]);
  const [calc, setCalc] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/master/wards").then((r) => setWards(r.data.items || []));
    api.get("/master/municipal-bodies").then((r) => setBodies(r.data.items || []));
  }, []);

  useEffect(() => {
    api.get("/sewerage/calculator", { params: {
      connection_type: form.connection_type,
      connection_size_mm: Number(form.connection_size_mm) || 100,
      seats: Number(form.number_of_seats) || 1,
      months: 1,
    }}).then((r) => setCalc(r.data));
  }, [form.connection_type, form.connection_size_mm, form.number_of_seats]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form,
        built_up_area: Number(form.built_up_area),
        number_of_seats: Number(form.number_of_seats),
        connection_size_mm: Number(form.connection_size_mm),
      };
      const r = await api.post("/sewerage/consumers", payload);
      toast.success("Sewerage connection registered", { description: r.data.connection_id });
      nav(`/sewerage/${r.data.id}`);
    } catch (err) {
      toast.error("Registration failed", { description: err.response?.data?.detail || "Try again" });
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-6 max-w-4xl" data-testid="sewerage-register-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Register</div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold flex items-center gap-2">
          <Waves className="w-6 h-6 text-[#0D7650]" /> New Sewerage Connection
        </h1>
      </div>

      <Card>
        <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <Label>Consumer Name *</Label>
            <Input value={form.consumer_name} onChange={(e) => set("consumer_name", e.target.value)} required data-testid="input-consumer-name" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={form.consumer_email} onChange={(e) => set("consumer_email", e.target.value)} data-testid="input-consumer-email" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={form.consumer_phone} onChange={(e) => set("consumer_phone", e.target.value)} />
          </div>
          <div>
            <Label>Ward *</Label>
            <Select value={form.ward} onValueChange={(v) => set("ward", v)}>
              <SelectTrigger data-testid="select-ward"><SelectValue /></SelectTrigger>
              <SelectContent>{wards.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>Address *</Label>
            <Input value={form.address} onChange={(e) => set("address", e.target.value)} required data-testid="input-address" />
          </div>
          <div>
            <Label>Connection Type *</Label>
            <Select value={form.connection_type} onValueChange={(v) => set("connection_type", v)}>
              <SelectTrigger data-testid="select-connection-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="domestic">Domestic</SelectItem>
                <SelectItem value="commercial">Commercial</SelectItem>
                <SelectItem value="industrial">Industrial</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Property Type</Label>
            <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="residential">Residential</SelectItem>
                <SelectItem value="commercial">Commercial</SelectItem>
                <SelectItem value="industrial">Industrial</SelectItem>
                <SelectItem value="mixed">Mixed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Built-up Area (sqft)</Label>
            <Input type="number" value={form.built_up_area} onChange={(e) => set("built_up_area", e.target.value)} />
          </div>
          <div>
            <Label>Number of Seats / WCs</Label>
            <Input type="number" value={form.number_of_seats} onChange={(e) => set("number_of_seats", e.target.value)} data-testid="input-seats" />
          </div>
          <div>
            <Label>Connection Size (mm)</Label>
            <Input type="number" value={form.connection_size_mm} onChange={(e) => set("connection_size_mm", e.target.value)} data-testid="input-size-mm" />
          </div>
          <div>
            <Label>Municipal Body</Label>
            <Select value={form.municipal_body} onValueChange={(v) => set("municipal_body", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{bodies.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {calc && (
        <Card className="bg-[#0F2942] text-white" data-testid="sewerage-fee-card">
          <CardContent className="p-6">
            <div className="text-xs uppercase tracking-widest opacity-70">Estimated Monthly Charge</div>
            <div className="text-4xl font-heading font-bold mt-2">{formatINR(calc.monthly)}</div>
            <div className="text-sm opacity-80 mt-1">Base ₹{calc.breakdown.base_monthly} + Size ₹{calc.breakdown.size_component} + Seats ₹{calc.breakdown.seat_component}</div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={() => nav("/sewerage")}>Cancel</Button>
        <Button type="submit" disabled={saving} className="bg-[#0F2942] hover:bg-[#1E3A5F] text-white" data-testid="btn-submit-sewerage">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Register Connection"}
        </Button>
      </div>
    </form>
  );
}
